import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import type { ProviderShapeSpec } from '../providers/types';
import { ALL_PROVIDER_SHAPE_SPECS } from '../providers/providerDialectRegistry';
import { getProviderIconUrl } from '../providers/providerIconResolver';
import { escapeXml, safeHex } from './svgSafe';

const providerNodeProps = z.object({
  name: z.string().default(''),
  resourceType: z.string().default('resource'),
  semanticType: z.string().default('artifact'),
  resourceId: z.string().default(''),
  service: z.string().default(''),
  hostingProvider: z.string().default('generic'),
  runtimeProvider: z.string().optional(),
  dialectId: z.string().default('generic'),
  providerLocalType: z.string().optional(),
  managedService: z.string().optional(),
  region: z.string().optional(),
  az: z.string().optional(),
  encrypted: z.boolean().optional(),
  publicExposure: z.enum(['none', 'restricted', 'open']).optional(),
  monthlySpend: z.number().optional(),
  fillColor: z.string().default('#FFFFFF'),
  strokeColor: z.string().default('#94a3b8'),
  textColor: z.string().default('#1e293b'),
  accentColor: z.string().default('#64748b'),
  iconUrl: z.string().optional(),
  stack: z.object({ count: z.number().int().nonnegative() }).optional(),
});

export type ProviderNodeProps = z.infer<typeof providerNodeProps>;

function createProviderManifest(shapeSpec: ProviderShapeSpec): ShapeManifest<ProviderNodeProps> {
  const iconUrl = getProviderIconUrl(shapeSpec) ?? undefined;
  const dims = shapeSpec.dimensions ?? (shapeSpec.container ? { width: 360, height: 220 } : { width: 180, height: 64 });

  return {
    id: shapeSpec.id,
    version: '1.0.0',
    category: 'network',
    icon: 'box',
    propsSchema: providerNodeProps,
    defaultProps: () => ({
      name: shapeSpec.label,
      resourceType: shapeSpec.localType,
      semanticType: shapeSpec.semanticType,
      resourceId: '',
      service: shapeSpec.localType,
      hostingProvider: shapeSpec.hostingProvider,
      runtimeProvider: shapeSpec.runtimeProvider,
      dialectId: shapeSpec.dialectId,
      providerLocalType: shapeSpec.localType,
      fillColor: '#FFFFFF',
      strokeColor: '#94a3b8',
      textColor: '#1e293b',
      accentColor: shapeSpec.color,
      iconUrl,
    }),
    defaultDimensions: dims,
    anchors: {
      snap: ['top', 'right', 'bottom', 'left', 'center'],
      perimeter: 'rect',
    },
    capabilities: {
      selectable: true,
      transformable: true,
      textEditable: true,
      connectable: true,
    },
    properties: [
      { key: 'name', label: 'Name', kind: 'string', required: true, ui: { group: 'identity', priority: 1 } },
      { key: 'resourceType', label: 'Provider type', kind: 'string', ui: { group: 'identity', priority: 2 } },
      { key: 'resourceId', label: 'Resource ID', kind: 'string', ui: { group: 'identity', priority: 3 } },
      { key: 'region', label: 'Region', kind: 'string', ui: { group: 'identity', priority: 4 } },
    ],
    acl: () => true,
    renderers: {
      svg: (args: { viewModel: unknown; props: unknown }) =>
        renderProviderNodeSvg(
          args.viewModel as { width?: number; height?: number },
          args.props as Record<string, unknown>,
          shapeSpec,
        ),
    },
  };
}

function renderProviderNodeSvg(
  viewModel: { width?: number; height?: number },
  props: Record<string, unknown>,
  shapeSpec: ProviderShapeSpec,
): string {
  const w = viewModel.width ?? shapeSpec.dimensions?.width ?? 180;
  const h = viewModel.height ?? shapeSpec.dimensions?.height ?? 64;
  const name = String(props.name || shapeSpec.label);
  const type = String(props.resourceType || shapeSpec.localType);
  const accent = safeHex(props.accentColor, shapeSpec.color);
  const fill = safeHex(props.fillColor, '#FFFFFF');
  const stroke = safeHex(props.strokeColor, '#94a3b8');
  const textColor = safeHex(props.textColor, '#1e293b');
  const escapedName = escapeXml(name);
  const escapedType = escapeXml(shortType(type, shapeSpec));
  const iconUrl = safeIconUrl(props.iconUrl);

  if (shapeSpec.container) {
    const typeTag = escapedType ? `<tspan font-weight="700" fill="#ffffff" opacity="0.78">${escapedType}</tspan>  ` : '';
    const icon = iconUrl
      ? `<image href="${escapeXml(iconUrl)}" x="10" y="7" width="18" height="18" preserveAspectRatio="xMidYMid meet" />`
      : `<circle cx="19" cy="16" r="8" fill="#ffffff" opacity="0.24" />`;
    return [
      `<rect x="0" y="0" width="${w}" height="${h}" rx="8" ry="8" fill="${fill}" stroke="${accent}" stroke-width="2" stroke-dasharray="8 4" />`,
      `<rect x="0" y="0" width="${w}" height="32" rx="8" ry="8" fill="${accent}" />`,
      `<rect x="0" y="16" width="${w}" height="16" fill="${accent}" />`,
      icon,
      `<text x="${iconUrl ? 36 : 12}" y="22" font-size="12" font-weight="600" fill="#ffffff" font-family="Arial, sans-serif">${typeTag}${escapedName}</text>`,
    ].join('');
  }

  const stack =
    props.stack && typeof props.stack === 'object'
      ? (props.stack as { count?: number })
      : null;
  const stackCount = stack && typeof stack.count === 'number' && stack.count > 1 ? stack.count : 0;

  const iconSize = 34;
  const iconX = 12;
  const iconY = Math.round((h - iconSize) / 2);
  const iconCenterX = iconX + iconSize / 2;
  const iconCenterY = iconY + iconSize / 2;
  const initials = escapeXml(labelInitials(shapeSpec.label));

  const stackBehind: string[] = [];
  if (stackCount > 0) {
    stackBehind.push(
      `<rect x="8" y="8" width="${w}" height="${h - 18}" rx="6" ry="6" fill="${fill}" stroke="${stroke}" stroke-width="1" opacity="0.72" />`,
      `<rect x="4" y="4" width="${w}" height="${h - 18}" rx="6" ry="6" fill="${fill}" stroke="${stroke}" stroke-width="1" opacity="0.82" />`,
    );
  }

  const iconMarkup = iconUrl
    ? [
        `<rect x="${iconX}" y="${iconY}" width="${iconSize}" height="${iconSize}" rx="8" ry="8" fill="#ffffff" stroke="${accent}" stroke-width="1.25" />`,
        `<image href="${escapeXml(iconUrl)}" x="${iconX + 5}" y="${iconY + 5}" width="${iconSize - 10}" height="${iconSize - 10}" preserveAspectRatio="xMidYMid meet" />`,
      ].join('')
    : [
        `<rect x="${iconX}" y="${iconY}" width="${iconSize}" height="${iconSize}" rx="8" ry="8" fill="${accent}" />`,
        `<text x="${iconCenterX}" y="${iconCenterY + 4}" text-anchor="middle" font-size="9" font-weight="700" fill="#ffffff" font-family="Arial, sans-serif">${initials}</text>`,
      ].join('');

  const countBadge = stackCount > 0
    ? (() => {
        const label = `x${stackCount}`;
        const badgeW = Math.max(24, 10 + label.length * 6.5);
        const badgeX = w - badgeW - 6;
        return [
          `<rect x="${badgeX}" y="6" width="${badgeW}" height="18" rx="9" ry="9" fill="${accent}" />`,
          `<text x="${badgeX + badgeW / 2}" y="19" text-anchor="middle" font-size="11" font-weight="700" fill="#ffffff" font-family="Arial, sans-serif">${escapeXml(label)}</text>`,
        ].join('');
      })()
    : '';

  return [
    ...stackBehind,
    `<rect x="0" y="0" width="${w}" height="${h}" rx="7" ry="7" fill="${fill}" stroke="${stroke}" stroke-width="1.5" />`,
    `<rect x="0" y="0" width="4" height="${h}" rx="2" ry="2" fill="${accent}" />`,
    iconMarkup,
    `<text x="${iconX + iconSize + 12}" y="${Math.round(h / 2 - 5)}" font-size="12" font-weight="600" fill="${textColor}" font-family="Arial, sans-serif">${escapedName}</text>`,
    `<text x="${iconX + iconSize + 12}" y="${Math.round(h / 2 + 12)}" font-size="10" fill="#64748b" font-family="Arial, sans-serif">${escapedType}</text>`,
    countBadge,
  ].join('');
}

function safeIconUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (value.startsWith('/assets/network-icons/')) return value;
  return null;
}

function shortType(type: string, shapeSpec: ProviderShapeSpec): string {
  if (shapeSpec.dialectId === 'azure') {
    return type.split('/').slice(-1)[0]?.replace(/([a-z])([A-Z])/g, '$1 $2') ?? type;
  }
  if (shapeSpec.dialectId === 'gcp') {
    return type.split('/').slice(-1)[0] ?? type;
  }
  return type.replace(/[_-]+/g, ' ');
}

function labelInitials(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
}

export const ProviderShapeManifests = ALL_PROVIDER_SHAPE_SPECS.map(createProviderManifest);
